/**
 * The values an enumerated field accepts, fetched once per field.
 *
 * These come from the MODEL, not from BEST: the profile's scoping axes are
 * abstract and each program supplies its own enum, so the only way to know
 * what a segment may be is to ask.
 *
 * Cached per field, and the cache is now INVALIDATED rather than permanent: it
 * used to say these do not change while the page is open, and "+ Add Option"
 * made that false. `forgetChoices` is called after a value is added.
 *
 * Adding clears EVERY field, not just the one edited, because one vocabulary
 * can serve several: the library declares one `PropellantKind` and both `fuel`
 * and `oxidizer` draw on it, so a propellant added through one appears in the
 * other. Clearing selectively would leave the sibling showing a stale list.
 *
 * Two things issue #91 changed, both found on a remote connection:
 *
 * - The cache is keyed by PROGRAM AND BRANCH as well as the field. It was keyed
 *   by the field alone, so after swapping program a form offered the segments
 *   of the program just left. Each program declares its own.
 * - Only an ANSWER is cached. Every failure used to become an empty list for
 *   the life of the page, so one dropped request on a VPN left the menus empty
 *   until a reload - which is the "cleared on a cache clear" in the report.
 *   Now only a refusal (the field is not enumerated) is kept as empty; anything
 *   else is reported as `failed`, forgotten, and asked again by `retry`.
 */

import { useContext, useEffect, useState } from 'react'
import { ApiError, api } from '../api'
import type { FieldSchema } from '../schema'
import { ProjectContext } from '../state/ProjectContext'

const cache = new Map<string, Promise<string[]>>()

/** Forget everything, so a test starts from nothing. */
export function resetChoices() {
  cache.clear()
}

/**
 * Forget every cached list, after one of them has grown.
 *
 * Named apart from `resetChoices` even though it does the same thing: one is a
 * test's fixture reset and the other is production behaviour, and a later
 * change to either should not silently change the other.
 */
export function forgetChoices() {
  cache.clear()
}

/**
 * True for the answer that means "this field takes no enumerated value".
 *
 * A fact about the field, so it is kept as an empty list and the control falls
 * back to free text. Anything else - a dropped connection, a 401 while the
 * session renews, a 5xx while a branch database is being read - is about the
 * REQUEST, and keeping it would keep the failure.
 */
function isRefusal(error: unknown): boolean {
  return error instanceof ApiError && [400, 404, 422].includes(error.status)
}

function load(scope: string, kind: string, field: string): Promise<string[]> {
  const key = `${scope}|${kind}/${field}`
  let pending = cache.get(key)
  if (!pending) {
    const asked: Promise<string[]> = api.choices(kind, field).catch((error: unknown) => {
      if (isRefusal(error)) return []
      // Only this request's own entry: a forget-and-ask in the meantime has
      // put a newer one there, which is not ours to remove.
      if (cache.get(key) === asked) cache.delete(key)
      throw error
    })
    pending = asked
    cache.set(key, pending)
  }
  return pending
}

export interface Choices {
  options: string[]
  loading: boolean
  /** True when the list could not be read. `options` is empty, and is not the answer. */
  failed: boolean
  /** Ask again, after a failure. */
  retry: () => void
}

export function useChoices(kind: string, field: FieldSchema | null): Choices {
  // A suggested text field asks too: its answer is what is already in use.
  const wanted =
    field && (field.kind === 'enum' || field.kind === 'metadata_list' || Boolean(field.suggested))
  const [state, setState] = useState({ options: [] as string[], loading: Boolean(wanted), failed: false })
  const [attempt, setAttempt] = useState(0)

  // Read without `useProject`, which throws outside a provider: a field
  // rendered on its own (a test, a dialog mounted bare) is still a field.
  const project = useContext(ProjectContext)
  const scope = `${project?.current?.slug ?? ''}@${project?.branch ?? ''}`
  // Until the program is known the key is not, and asking then means asking
  // twice: once under no program, and again when it resolves.
  const settled = !project?.loading

  useEffect(() => {
    if (!wanted || !field) {
      setState({ options: [], loading: false, failed: false })
      return
    }
    setState({ options: [], loading: true, failed: false })
    if (!settled) return
    let live = true
    load(scope, kind, field.name).then(
      (options) => {
        if (live) setState({ options, loading: false, failed: false })
      },
      () => {
        if (live) setState({ options: [], loading: false, failed: true })
      },
    )
    return () => {
      live = false
    }
  }, [scope, settled, kind, field?.name, wanted, attempt])

  return { ...state, retry: () => setAttempt((count) => count + 1) }
}
